import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, CheckCircle, XCircle, Clock, Download, RotateCcw, Trash2, SkipForward } from "lucide-react";

const STATUS_COLORS = {
  completed: "#059669", running: "#006080", failed: "#DC2626",
  pending: "#D97706", paused: "#6B7280", stopped: "#9CA3AF", skipped: "#7C3AED",
};

export default function ExecutionListPage() {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadExecutions();
    const interval = setInterval(loadExecutions, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadExecutions = async () => {
    try {
      const res = await client.get("/executions/");
      setExecutions(res.data);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === executions.length) setSelected(new Set());
    else setSelected(new Set(executions.map(e => e._id)));
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} execution(s)?`)) return;
    setDeleting(true);
    try {
      await client.post("/executions/bulk-delete", { ids: [...selected] });
      setSelected(new Set());
      await loadExecutions();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Executions</h1>
          <p className="text-gray-500 text-sm mt-0.5">History of all API execution runs</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button
              variant="outline"
              className="text-red-500 hover:bg-red-50 hover:border-red-200 h-9"
              onClick={bulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} className="mr-1.5" />
              {deleting ? "Deleting..." : `Delete ${selected.size} selected`}
            </Button>
          )}
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => navigate("/execute")}>
            <Play size={15} className="mr-2" /> New Execution
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Play size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No executions yet</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => navigate("/execute")}>
            Run your first execution
          </Button>
        </div>
      ) : (
        <>
          {/* Select all row */}
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <input
              type="checkbox"
              className="w-4 h-4 rounded"
              checked={selected.size === executions.length && executions.length > 0}
              onChange={selectAll}
              data-testid="select-all-checkbox"
            />
            <span className="text-xs text-gray-400">
              {selected.size > 0 ? `${selected.size} selected` : "Select all"}
            </span>
          </div>
          <div className="space-y-2">
            {executions.map(e => (
              <div
                key={e._id}
                className={`bg-white border rounded-lg p-4 hover:shadow-sm transition-all ${selected.has(e._id) ? "border-[#006080] bg-[#F0F8FA]" : "border-gray-200"}`}
                data-testid={"execution-row-" + e._id}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded flex-shrink-0"
                    checked={selected.has(e._id)}
                    onChange={() => toggleSelect(e._id)}
                    onClick={ev => ev.stopPropagation()}
                  />
                  <div
                    className="flex-1 flex items-center gap-3 cursor-pointer min-w-0"
                    onClick={() => navigate(`/executions/${e._id}`)}
                  >
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[e.status] || "#9CA3AF" }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-800">
                          {e.dry_run ? "[Dry Run] " : ""}{e.processed_count}/{e.total_rows} rows processed
                        </span>
                        <Badge
                          className="text-xs"
                          style={{ background: `${STATUS_COLORS[e.status]}15`, color: STATUS_COLORS[e.status], border: `1px solid ${STATUS_COLORS[e.status]}30` }}
                        >
                          {e.status}
                        </Badge>
                      </div>
                      <div className="flex gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={10} /> {e.success_count} ok</span>
                        <span className="text-xs text-red-500 flex items-center gap-1"><XCircle size={10} /> {e.error_count} failed</span>
                        {e.avg_response_time_ms && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={10} /> {Math.round(e.avg_response_time_ms)}ms avg</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                  </div>
                  <div className="flex gap-1" onClick={ev => ev.stopPropagation()}>
                    {e.status === "completed" && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" title="Export log CSV" onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/executions/${e._id}/export`, "_blank");
                      }}>
                        <Download size={12} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs text-red-400 hover:bg-red-50 hover:border-red-200"
                      title="Delete"
                      onClick={async () => {
                        if (!window.confirm("Delete this execution?")) return;
                        await client.post("/executions/bulk-delete", { ids: [e._id] });
                        loadExecutions();
                      }}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
